/**
 * Files Query Hooks (v2)
 * React Query hooks for DB-tracked file management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { filesService } from 'src/api/services';
import { useToast } from 'src/Contexts/ToastContext';
import { queryKeys } from './queryKeys';

/**
 * Fetch site images for an installation
 */
export function useInstallationFilesQuery(installationId: string | null) {
  return useQuery({
    queryKey: queryKeys.files.byInstallation(installationId || ''),
    queryFn: () => filesService.listByInstallation(installationId!),
    enabled: !!installationId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch documents for a project
 */
export function useProjectFilesQuery(projectId: string | null) {
  return useQuery({
    queryKey: queryKeys.files.byProject(projectId || ''),
    queryFn: () => filesService.listByProject(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Upload a site image to an installation
 */
export function useUploadInstallationFileMutation(installationId: string | null) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (file: File) => filesService.uploadToInstallation(installationId!, file),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.files.all });
      toast.success('Image uploaded successfully');
    },

    onError: (err) => {
      toast.error('Failed to upload image');
      if (import.meta.env.DEV) {
        console.error('Upload image error:', err);
      }
    },
  });
}

/**
 * Upload a document to a project
 */
export function useUploadProjectFileMutation(projectId: string | null) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (file: File) => filesService.uploadToProject(projectId!, file),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.files.all });
      toast.success('Document uploaded successfully');
    },

    onError: (err) => {
      toast.error('Failed to upload document');
      if (import.meta.env.DEV) {
        console.error('Upload document error:', err);
      }
    },
  });
}

/**
 * Delete a file (works for both images and documents)
 */
export function useDeleteFileMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (fileId: string) => filesService.delete(fileId),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.files.all });
      toast.success('File deleted successfully');
    },

    onError: (err) => {
      toast.error('Failed to delete file');
      if (import.meta.env.DEV) {
        console.error('Delete file error:', err);
      }
    },
  });
}
